// iter179 — Tutorial Videos screen (dynamic).
//
// Content is fetched from `GET /api/config/tutorials?property_id=<id>`,
// which proxies booking-vault-admin. The API returns:
//   [{id, question, answer, youtube_url, display_order, enabled}]
// filtered to enabled=true and sorted by display_order.
//
// Every visit to this screen refetches so admin edits show up without
// requiring a redeploy or logout.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, PlayCircle, RefreshCw, ExternalLink, Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const openYoutube = (url) => {
  if (!url) return;
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    /* ignore */
  }
};

const TutorialVideosScreen = () => {
  const navigate = useNavigate();
  const { selectedProperty } = useAuth();
  const propertyId = selectedProperty?.id || selectedProperty?.property_id || '';

  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [source, setSource] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = `${API}/config/tutorials${propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : ''}`;
      const res = await axios.get(url, { timeout: 10000 });
      const payload = res?.data?.RESULT || {};
      setTutorials(Array.isArray(payload.tutorials) ? payload.tutorials : []);
      setSource(payload?.meta?.source || '');
    } catch (e) {
      setError('Could not load tutorials. Please try again.');
      setTutorials([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-white pb-24" data-testid="tutorial-videos-screen">
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center gap-3 px-4 py-3 max-w-2xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="tutorial-back-btn"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <PlayCircle className="w-5 h-5 text-[#1B2A7B]" />
            <h1 className="text-base font-semibold text-gray-900">Tutorial Videos</h1>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            data-testid="tutorial-refresh-btn"
            aria-label="Refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin text-gray-600" /> : <RefreshCw className="w-4 h-4 text-gray-600" />}
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {loading && tutorials.length === 0 && (
          <div className="flex flex-col items-center py-16 text-gray-500" data-testid="tutorial-loading">
            <Loader2 className="w-8 h-8 animate-spin mb-3 text-[#1B2A7B]" />
            <p className="text-sm">Loading tutorials…</p>
          </div>
        )}

        {!loading && error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="tutorial-error">
            {error}
          </div>
        )}

        {!loading && !error && tutorials.length === 0 && (
          <div className="max-w-md mx-auto px-2 py-12 text-center" data-testid="tutorial-empty">
            <div className="w-20 h-20 rounded-2xl bg-[#EEF1FC] flex items-center justify-center mx-auto mb-5">
              <Sparkles className="w-10 h-10 text-[#1B2A7B]" />
            </div>
            <h2 className="text-lg font-semibold text-[#1B2A7B] mb-1">No tutorials yet</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              Your team hasn&apos;t published any tutorials for this property yet.
              Once they do, they&apos;ll appear here automatically — no app update needed.
            </p>
          </div>
        )}

        {!loading && tutorials.length > 0 && (
          <ul className="space-y-4" data-testid="tutorial-list">
            {tutorials.map((t) => (
              <li
                key={t.id || `${t.display_order}-${t.question}`}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
                data-testid="tutorial-item"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#EEF1FC] flex items-center justify-center flex-shrink-0">
                    <PlayCircle className="w-5 h-5 text-[#1B2A7B]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold text-[#1B2A7B]/70 uppercase tracking-wider mb-0.5">Question</p>
                    <h3 className="text-sm font-semibold text-gray-900 leading-snug" data-testid="tutorial-question">
                      {t.question || '—'}
                    </h3>

                    {t.answer ? (
                      <>
                        <p className="text-[11px] font-semibold text-[#1B2A7B]/70 uppercase tracking-wider mt-3 mb-0.5">Answer</p>
                        <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap" data-testid="tutorial-answer">
                          {t.answer}
                        </p>
                      </>
                    ) : null}

                    {t.youtube_url ? (
                      <button
                        type="button"
                        onClick={() => openYoutube(t.youtube_url)}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1B2A7B] text-white text-xs font-medium hover:bg-[#152163] active:scale-[0.98] transition"
                        data-testid="tutorial-watch-btn"
                      >
                        <PlayCircle className="w-4 h-4" />
                        Watch Tutorial
                        <ExternalLink className="w-3 h-3 opacity-80" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {source && source !== 'admin_panel' && !loading && (
          <p className="mt-6 text-[11px] text-gray-400 text-center" data-testid="tutorial-source-note">
            {source === 'defaults_admin_panel_unreachable'
              ? 'Unable to reach the tutorial service right now.'
              : `source: ${source}`}
          </p>
        )}
      </div>
    </div>
  );
};

export default TutorialVideosScreen;
